import { makeVar, useReactiveVar } from '@apollo/client';



export interface IShopifyAuthState {
  token: string | null;
  timestamp: string | null;
  email: string | null;
  isValid: boolean;
}

const shopifyAuthStateVar = makeVar<IShopifyAuthState>({
  token: null,
  timestamp: null,
  email: null,
  isValid: false
});

// Token expiry time: 1 hour in seconds (matching backend)
const TOKEN_EXPIRY_SECONDS = 3600;

/**
 * Checks if a timestamp is expired (older than 1 hour)
 * @param timestamp - Timestamp string (Unix timestamp in seconds from PHP time())
 * @returns true if expired, false otherwise
 */
function __isTokenExpired(timestamp: string | null): boolean {
  if (!timestamp) return true;

  try {
    // WordPress PHP time() returns Unix timestamp in seconds
    const timestampSeconds = parseInt(timestamp, 10);
    if (!isNaN(timestampSeconds)) {
      const currentTimeSeconds = Math.floor(Date.now() / 1000);
      const timeDiff = currentTimeSeconds - timestampSeconds;
      return timeDiff > TOKEN_EXPIRY_SECONDS || timeDiff < 0;
    }

    // If we can't parse it, consider it expired
    return true;
  } catch {
    return true;
  }
}

export function initializeShopifyAuthFromURL() {
  // Ensure we're in browser environment
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const timestamp = urlParams.get('timestamp');
    const email = urlParams.get('email');

    if (token && timestamp && email) {
      // Always use new params from URL (they're fresh)
      // Store in sessionStorage for persistence across page navigations
      try {
        sessionStorage.setItem('shopify_auth_token', token);
        sessionStorage.setItem('shopify_auth_timestamp', timestamp);
        sessionStorage.setItem('shopify_auth_email', email);
      } catch (e) {
        console.error('[Shopify Auth] Failed to store in sessionStorage:', e);
      }

      shopifyAuthStateVar({
        token,
        timestamp,
        email,
        isValid: true
      });
    } else {
      // Try to restore from sessionStorage
      const storedToken = sessionStorage.getItem('shopify_auth_token');
      const storedTimestamp = sessionStorage.getItem('shopify_auth_timestamp');
      const storedEmail = sessionStorage.getItem('shopify_auth_email');

      if (storedToken && storedTimestamp && storedEmail) {
        // Check if the stored token is expired
        if (__isTokenExpired(storedTimestamp)) {
          // Token expired, clear auth state
          clearShopifyAuth();
        } else {
          // Token is still valid, restore it
          shopifyAuthStateVar({
            token: storedToken,
            timestamp: storedTimestamp,
            email: storedEmail,
            isValid: true
          });
        }
      }
    }
  } catch (error) {
    console.error('[Shopify Auth] Error initializing:', error);
  }
}



export function useShopifyAuthState() {
  return useReactiveVar(shopifyAuthStateVar);
}

// Get current WordPress auth state synchronously (for use in callbacks)
export function getShopifyAuthState(): IShopifyAuthState {
  const state = shopifyAuthStateVar();

  // If state is empty, try to initialize from sessionStorage one more time
  if (!state.token && !state.timestamp && typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    const storedToken = sessionStorage.getItem('shopify_auth_token');
    const storedTimestamp = sessionStorage.getItem('shopify_auth_timestamp');
    const storedEmail = sessionStorage.getItem('shopify_auth_email');

    if (storedToken && storedTimestamp && storedEmail && !__isTokenExpired(storedTimestamp)) {
      shopifyAuthStateVar({
        token: storedToken,
        timestamp: storedTimestamp,
        email: storedEmail,
        isValid: true
      });
      return shopifyAuthStateVar();
    }
  }

  // Check if token is expired and update isValid accordingly
  if (state.token && state.timestamp && state.email) {
    const expired = __isTokenExpired(state.timestamp);
    if (expired && state.isValid) {
      //   // Token expired, clear auth state
      window.alert('Shopify Token Expired, Please visit the webiste and open the order again');
      clearShopifyAuth();
      return shopifyAuthStateVar();
    }
    // Update isValid based on expiry status
    if (state.isValid !== !expired) {
      shopifyAuthStateVar({
        ...state,
        isValid: !expired
      });
      return shopifyAuthStateVar();
    }
  }
  return state;
}



export function clearShopifyAuth() {
  sessionStorage.removeItem('shopify_auth_token');
  sessionStorage.removeItem('shopify_auth_timestamp');
  sessionStorage.removeItem('shopify_auth_email');
  shopifyAuthStateVar({
    token: null,
    timestamp: null,
    email: null,
    isValid: false
  });
}
